import { clearStoredAttachments } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatAttachmentStore'
import { resetStagedTagPicks } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatTagPicks'

const OWNER_KEY = 'gecko_docchat_storage_owner'
const SESSION_KEY_PREFIXES = [
  'gecko_docpage_tabs',
  'gecko_docpage_chatbot',
  'gecko_docpage_chat_composer_drafts',
  'docChatTagPicks',
]

let currentOwner: string | null = null

/**
 * Chat tabs, drafts, and attachments hold transcript content, and browser
 * storage outlives a login. Readers get nothing until the signed-in user is
 * known, and a different user wipes what the previous one left behind.
 */
export function getDocChatStorageOwner(): string | null {
  return currentOwner
}

/**
 * Flushes can run after the owner changes (unmount cleanup of a tree rendered
 * for the previous user), so writers pass the owner they rendered for and
 * write nothing unless it still owns storage.
 */
export function isCurrentDocChatStorageOwner(
  owner: string | null
): owner is string {
  return owner !== null && owner === currentOwner
}

export function setDocChatStorageOwner(userId: string | undefined): void {
  const nextOwner = userId || null
  if (nextOwner === currentOwner) return
  currentOwner = nextOwner
  if (!nextOwner) return
  claimStorage(sessionStorageOrNull(), nextOwner, clearDocChatSessionStorage)
  claimStorage(localStorageOrNull(), nextOwner, () => {
    void clearStoredAttachments()
  })
}

/** Session and local storage have separate lifetimes, so each tracks its own owner. */
function claimStorage(
  storage: Storage | null,
  owner: string,
  clear: () => void
): void {
  if (!storage) return
  try {
    if (storage.getItem(OWNER_KEY) === owner) return
    clear()
    storage.setItem(OWNER_KEY, owner)
  } catch {
    clear()
  }
}

function clearDocChatSessionStorage(): void {
  resetStagedTagPicks()
  const storage = sessionStorageOrNull()
  if (!storage) return
  try {
    const keys = Array.from({ length: storage.length }, (_, index) =>
      storage.key(index)
    )
    keys
      .filter((key): key is string =>
        SESSION_KEY_PREFIXES.some((prefix) => key?.startsWith(prefix))
      )
      .forEach((key) => storage.removeItem(key))
  } catch {
    return
  }
}

function sessionStorageOrNull(): Storage | null {
  return typeof sessionStorage === 'undefined' ? null : sessionStorage
}

function localStorageOrNull(): Storage | null {
  return typeof localStorage === 'undefined' ? null : localStorage
}
